/**
 * useFilePicker Hook
 * Browser-based file picker replacing Electron dialog.openFile
 */

import { useCallback } from 'react';

interface FilePickerOptions {
  accept?: string; // MIME types or extensions (e.g., '.las,.laz,.e57')
  multiple?: boolean;
}

interface FilePickerResult {
  canceled: boolean;
  files: File[];
}

export function useFilePicker() {
  const pickFiles = useCallback((options?: FilePickerOptions): Promise<FilePickerResult> => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';

      if (options?.accept) {
        input.accept = options.accept;
      }
      if (options?.multiple) {
        input.multiple = true;
      }

      input.addEventListener('change', () => {
        const files = Array.from(input.files || []);
        input.remove();
        resolve({ canceled: files.length === 0, files });
      });

      input.addEventListener('cancel', () => {
        input.remove();
        resolve({ canceled: true, files: [] });
      });

      document.body.appendChild(input);
      input.click();
    });
  }, []);

  /** Pick an entire directory. Returns files with webkitRelativePath set. */
  const pickDirectory = useCallback((): Promise<FilePickerResult> => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';
      input.webkitdirectory = true;

      input.addEventListener('change', () => {
        const files = Array.from(input.files || []);
        input.remove();
        resolve({ canceled: files.length === 0, files });
      });

      input.addEventListener('cancel', () => {
        input.remove();
        resolve({ canceled: true, files: [] });
      });

      document.body.appendChild(input);
      input.click();
    });
  }, []);

  return { pickFiles, pickDirectory };
}

/** File type accept strings for common formats */
export const FILE_ACCEPTS = {
  POINT_CLOUD: '.e57,.las,.laz',
  GEOTIFF: '.tif,.tiff',
  MODEL: '.gltf,.glb',
  BIM: '.ifc,.gltf,.glb',
  PDF: '.pdf',
  FLOOR_PLAN: '.pdf,.png,.jpg,.jpeg',
  PHOTO: '.jpg,.jpeg,.png,.webp,.heic,.heif',
  IMAGE: 'image/*',
} as const;
